import { useState, useCallback, useEffect, useRef } from 'react'
import './index.css'
import { badges, whyCards, flow, capabilities, philosophy, openSource, maintainer, cliSnippet, configSnippet, navigation, launchDate, demo } from './data/content'
import { config } from './config'

function FlipDigit({ value, label }) {
  const str = String(value).padStart(2, '0')
  const [cur, setCur] = useState(str)
  const [prev, setPrev] = useState(str)
  const [next, setNext] = useState(str)
  const [flipping, setFlipping] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    if (str === cur) return
    if (timerRef.current) clearTimeout(timerRef.current)
    setPrev(cur)
    setNext(str)
    setFlipping(true)
    timerRef.current = setTimeout(() => {
      setCur(str)
      setFlipping(false)
    }, 500)
    return () => clearTimeout(timerRef.current)
  }, [str])

  const cardH = 56
  const halfH = cardH / 2

  const NumInner = ({ val, isBottom }) => (
    <div style={{ height: cardH, display: 'flex', alignItems: 'center', justifyContent: 'center', transform: isBottom ? `translateY(-${halfH}px)` : undefined }}>
      <span className="text-3xl font-bold tabular-nums text-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{val}</span>
    </div>
  )

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ perspective: '400px', width: 56 }}>
        {/* top half — static current */}
        <div className="rounded-t-lg overflow-hidden border border-white/10 border-b-0" style={{ height: halfH, background: 'rgba(30,38,54,0.95)' }}>
          <NumInner val={cur} />
        </div>
        {/* divider shadow */}
        <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: halfH - 2, height: 4, background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.15))' }} />
        {/* bottom half — static current */}
        <div className="rounded-b-lg overflow-hidden border border-white/10 border-t-0" style={{ height: halfH, background: 'rgba(22,30,44,0.95)' }}>
          <NumInner val={cur} isBottom />
        </div>
        {/* flap: old top half flipping away */}
        {flipping && (
          <div className="flip-flap-top absolute top-0 left-0 right-0 rounded-t-lg overflow-hidden border border-white/10 border-b-0 z-10" style={{ height: halfH, background: 'rgba(30,38,54,0.95)' }}>
            <NumInner val={prev} />
          </div>
        )}
        {/* flap: new bottom half flipping in */}
        {flipping && (
          <div className="flip-flap-bottom absolute left-0 right-0 rounded-b-lg overflow-hidden border border-white/10 border-t-0 z-10" style={{ height: halfH, top: halfH, background: 'rgba(22,30,44,0.95)' }}>
            <NumInner val={next} isBottom />
          </div>
        )}
      </div>
      <span className="text-[10px] uppercase tracking-widest text-muted">{label}</span>
    </div>
  )
}

function App() {
  const [copyState, setCopyState] = useState('idle') // idle | copied | error
  const [copyConfigState, setCopyConfigState] = useState('idle') // idle | copied | error
  const [stars, setStars] = useState(null)
  const [countdown, setCountdown] = useState(null)

  useEffect(() => {
    const tick = () => {
      const diff = launchDate - Date.now()
      if (diff <= 0) { setCountdown(null); return }
      const d = Math.floor(diff / 86400000)
      const h = Math.floor((diff % 86400000) / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setCountdown({ d, h, m, s })
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const apiUrl = config.githubUrl.replace('https://github.com/', 'https://api.github.com/repos/')
    fetch(apiUrl)
      .then(r => r.json())
      .then(data => { if (typeof data.stargazers_count === 'number') setStars(data.stargazers_count) })
      .catch(() => {})
  }, [])

  const makeCopyHandler = (getText, setState) => async () => {
    const text = getText()
    const fallbackCopy = () => {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    try {
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
      } else {
        fallbackCopy()
      }
      setState('copied')
    } catch (err) {
      console.error('Copy failed', err)
      setState('error')
    } finally {
      setTimeout(() => setState('idle'), 2000)
    }
  }

  const copySnippet = useCallback(makeCopyHandler(() => cliSnippet.trim(), setCopyState), [])
  const copyConfig = useCallback(makeCopyHandler(() => configSnippet.trim(), setCopyConfigState), [])

  return (
    <div className="min-h-screen">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(63,216,196,0.08),transparent_25%),radial-gradient(circle_at_80%_10%,rgba(242,181,102,0.08),transparent_30%),#0b0f14]" aria-hidden />
      <div className="relative z-10 max-w-6xl px-6 py-8 lg:px-10 lg:py-12 mx-auto">
        <header className="glass-panel sticky top-4 z-20 mb-8 flex flex-col gap-3 border-white/10 bg-surface/90 px-4 py-3 backdrop-blur lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="h-4 w-4 rounded-md bg-gradient-to-br from-accent to-accentStrong shadow-[0_0_0_6px_rgba(63,216,196,0.15)]" aria-hidden />
            <div>
              <div className="text-sm uppercase tracking-[0.2em] text-muted">vulnreach</div>
              <div className="whitespace-nowrap text-xs text-muted">OWASP Project<span className="hidden xl:inline"> · Open-source reachability analysis</span></div>
            </div>
          </div>
          <nav aria-label="Primary" className="flex flex-wrap gap-1 text-sm text-muted lg:flex-nowrap">
            {navigation.map((item) => (
              <a key={item.label} className="whitespace-nowrap rounded-full px-3 py-1 hover:text-text hover:bg-white/5" href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>
          <div className="flex flex-wrap gap-2 lg:flex-nowrap lg:shrink-0">
            <a
              href={config.githubUrl}
              className="rounded-xl border border-white/10 px-4 py-2 text-sm font-semibold text-text hover:border-accent"
              target="_blank"
              rel="noreferrer"
            >
              View on GitHub
            </a>
            <a
              href="#snippet"
              className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-surface shadow-[0_10px_35px_rgba(63,216,196,0.35)] hover:bg-accentStrong"
            >
              Get Started
            </a>
          </div>
        </header>

        {countdown && (
          <div className="mb-6 glass-panel flex flex-col items-center gap-5 border-accent/30 bg-accent/5 px-8 py-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Launching in</span>
            <div className="flex items-end gap-3">
              <FlipDigit value={countdown.d} label="days" />
              <span className="text-2xl font-bold text-white/20 mb-6">:</span>
              <FlipDigit value={countdown.h} label="hours" />
              <span className="text-2xl font-bold text-white/20 mb-6">:</span>
              <FlipDigit value={countdown.m} label="min" />
              <span className="text-2xl font-bold text-white/20 mb-6">:</span>
              <FlipDigit value={countdown.s} label="sec" />
            </div>
          </div>
        )}

        <main className="space-y-14">
          <section id="hero" className="glass-panel grid gap-8 border-white/10 bg-panel/90 px-6 py-8 lg:grid-cols-2 lg:items-center">
            <div className="space-y-4">
              <p className="eyebrow">An official OWASP Project</p>
              <h1 className="text-4xl font-semibold leading-tight text-text sm:text-5xl">Find vulnerabilities that actually matter.</h1>
              <p className="text-lg text-muted">
                VulnReach is runtime-aware SCA. It correlates Trivy findings with taint tracking, call graphs, route exposure, and runtime execution to prove which CVEs are actually reachable, not just installed, and records the evidence in a Runtime Bill of Materials (RBOM).
              </p>
              <div className="flex flex-wrap gap-2">
                {badges.map((badge) => (
                  <span key={badge} className="badge-chip">
                    {badge}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={config.githubUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-surface shadow-[0_12px_35px_rgba(63,216,196,0.35)] hover:bg-accentStrong"
                >
                  View on GitHub
                </a>
                <a
                  href="#why"
                  className="rounded-xl border border-white/10 px-5 py-2.5 text-sm font-semibold text-text hover:border-accent"
                >
                  Learn More
                </a>
              </div>
            </div>
            <div className="glass-panel relative overflow-hidden border-white/10 bg-surface/60 p-6">
              <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-accent/10 blur-3xl" aria-hidden />
              <div className="space-y-4 text-sm text-muted">
                <div className="flex items-center gap-2 text-text">
                  <div className="h-2 w-2 rounded-full bg-amber" />
                  Live Reachability Trace
                </div>
                <div className="rounded-xl border border-white/5 bg-black/30 p-4 font-mono text-xs text-text/80">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-muted">analysis.run</span>
                    <span className="rounded-full bg-amber/20 px-2 py-1 text-amber text-[11px]">risk</span>
                  </div>
                  <div className="space-y-2">
                    <div className="rounded-lg bg-white/5 px-3 py-2">$ curl -X POST http://localhost:8000/scan -H "Authorization: Bearer $TOKEN" -d '{"{"}...{"}"}'</div>
                    <div className="rounded-lg bg-white/5 px-3 py-2">✓ SCA complete — Trivy found 74 CVEs</div>
                    <div className="rounded-lg bg-white/5 px-3 py-2">✓ Taint flows traced (tainter)</div>
                    <div className="rounded-lg bg-white/5 px-3 py-2">✓ Call graph + route exposure mapped</div>
                    <div className="rounded-lg bg-white/5 px-3 py-2">✓ Runtime coverage collected</div>
                    <div className="rounded-lg bg-amber/15 px-3 py-2 text-amber">CONFIRMED: CVE-2024-12345 reachable via /api/login (confidence 0.95)</div>
                    <div className="rounded-lg bg-white/5 px-3 py-2">policy.block_if matched → scan status: blocked</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="why" className="space-y-6">
            <div className="space-y-2">
              <p className="eyebrow">Why VulnReach?</p>
              <h2 className="text-3xl font-semibold text-text">From presence to reachability.</h2>
              <p className="text-muted">A concise, execution-aware view for AppSec and DevSecOps teams.</p>
            </div>
            <div className="card-grid">
              {whyCards.map((card) => (
                <article key={card.title} className="card-surface">
                  <h3 className="mb-2 text-lg font-semibold text-text">{card.title}</h3>
                  <p className="text-sm text-muted">{card.body}</p>
                </article>
              ))}
            </div>
          </section>

          <section id="how" className="space-y-5">
            <div className="space-y-2">
              <p className="eyebrow">How VulnReach Works</p>
              <h2 className="text-3xl font-semibold text-text">Signals stitched into a reachability graph.</h2>
            </div>
            <div className="flex flex-col gap-3 rounded-2xl border border-white/5 bg-panel/70 p-4 shadow-glow md:flex-row md:items-center">
              {flow.map((step, idx) => (
                <div key={step} className="flex items-center gap-3">
                  <div className="flow-step">
                    <span className="flow-dot" />
                    <span className="text-text">{step}</span>
                  </div>
                  {idx < flow.length - 1 && <div className="hidden h-px w-10 bg-white/10 md:block" aria-hidden />}
                </div>
              ))}
            </div>
          </section>

          <section id="demo" className="space-y-6">
            <div className="space-y-2">
              <p className="eyebrow">See It In Action</p>
              <h2 className="text-3xl font-semibold text-text">From 74 CVEs to the 50 that matter.</h2>
              <p className="text-muted">A real scan of an intentionally vulnerable app, from raw SCA output to a prioritised fix plan.</p>
            </div>
            <a
              href={demo.videoUrl}
              target="_blank"
              rel="noreferrer"
              className="group relative block overflow-hidden rounded-2xl border border-white/10 bg-black/40"
              aria-label="Watch the VulnReach demo on YouTube"
            >
              <img src={demo.poster} alt="" width="1280" height="720" loading="lazy" className="aspect-[21/9] w-full object-cover opacity-90 transition-opacity group-hover:opacity-100" />
              <span className="absolute inset-x-0 bottom-0 flex justify-center pb-6 sm:pb-10">
                <span className="flex items-center gap-3 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-surface shadow-[0_12px_35px_rgba(63,216,196,0.35)] transition-transform group-hover:scale-105">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden><path d="M8 5v14l11-7z" /></svg>
                  Watch the demo
                </span>
              </span>
            </a>
            <div className="grid gap-4 md:grid-cols-2">
              {demo.screenshots.map((shot) => (
                <figure key={shot.src} className="card-surface flex flex-col gap-3">
                  <a href={shot.src} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-xl border border-white/5 bg-black/40">
                    <img
                      src={shot.src}
                      alt={shot.title}
                      width={shot.width}
                      height={shot.height}
                      loading="lazy"
                      className={`aspect-[16/10] w-full object-top transition-transform duration-300 hover:scale-[1.02] ${shot.fit === 'contain' ? 'object-contain' : 'object-cover'}`}
                    />
                  </a>
                  <figcaption className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-widest text-accent">{shot.step}</p>
                    <p className="text-base font-semibold text-text">{shot.title}</p>
                    <p className="text-sm text-muted">{shot.caption}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>

          <section id="capabilities" className="space-y-6">
            <div className="space-y-2">
              <p className="eyebrow">Key Capabilities</p>
              <h2 className="text-3xl font-semibold text-text">Purpose-built for security engineers.</h2>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {capabilities.map((item) => (
                <div key={item} className="card-surface flex items-start gap-3">
                  <div className="mt-1 h-2 w-2 rounded-full bg-accent" />
                  <p className="text-sm text-text">{item}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="snippet" className="space-y-4">
            <div className="space-y-2">
              <p className="eyebrow">Quick Start</p>
              <h2 className="text-3xl font-semibold text-text">Start the server, then scan from the UI or curl.</h2>
            </div>
            <div className="glass-panel border border-white/10 bg-black/40 p-4">
              <div className="mb-3 flex items-center justify-between text-xs text-muted">
                <span>quick start</span>
                <button
                  type="button"
                  onClick={copySnippet}
                  className={`rounded-lg border px-3 py-1 font-semibold transition-colors ${
                    copyState === 'copied'
                      ? 'border-accent bg-accent/10 text-accent'
                      : copyState === 'error'
                        ? 'border-amber text-amber'
                        : 'border-white/10 text-text hover:border-accent'
                  }`}
                  aria-live="polite"
                >
                  {copyState === 'copied' ? '✓ Copied!' : copyState === 'error' ? 'Copy failed' : 'Copy'}
                </button>
              </div>
              <pre className="overflow-x-auto rounded-xl bg-black/60 p-4 text-sm text-text">{cliSnippet.trim()}</pre>
            </div>
            <div className="glass-panel border border-white/10 bg-black/40 p-4">
              <div className="mb-3 flex items-center justify-between text-xs text-muted">
                <span>scan.yml — sample config</span>
                <button
                  type="button"
                  onClick={copyConfig}
                  className={`rounded-lg border px-3 py-1 font-semibold transition-colors ${
                    copyConfigState === 'copied'
                      ? 'border-accent bg-accent/10 text-accent'
                      : copyConfigState === 'error'
                        ? 'border-amber text-amber'
                        : 'border-white/10 text-text hover:border-accent'
                  }`}
                  aria-live="polite"
                >
                  {copyConfigState === 'copied' ? '✓ Copied!' : copyConfigState === 'error' ? 'Copy failed' : 'Copy'}
                </button>
              </div>
              <pre className="overflow-x-auto rounded-xl bg-black/60 p-4 text-sm text-text">{configSnippet.trim()}</pre>
            </div>
          </section>

          <section id="trust" className="space-y-4">
            <div className="space-y-2">
              <p className="eyebrow">Trust & Philosophy</p>
              <h2 className="text-3xl font-semibold text-text">Built for engineers, not marketers.</h2>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {philosophy.map((item) => (
                <div key={item} className="card-surface text-sm text-text">{item}</div>
              ))}
              <div className="md:col-span-2 card-surface">
                <p className="text-muted">“If a vulnerability cannot be reached, it cannot be exploited.”</p>
              </div>
            </div>
          </section>

          <section id="open-source" className="space-y-4">
            <div className="space-y-2">
              <p className="eyebrow">Open Source</p>
              <h2 className="text-3xl font-semibold text-text">OSS-first, community-driven at OWASP.</h2>
            </div>
            <div className="card-surface flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-1">
                <p className="text-sm text-text font-semibold">
                  {stars !== null ? `★ ${stars.toLocaleString()} stars on GitHub` : '★ View on GitHub'}
                </p>
                <p className="text-sm text-muted">License: {openSource.license}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs text-muted uppercase tracking-widest">Maintainer</p>
                <a href={maintainer.site} target="_blank" rel="noreferrer" className="text-sm font-semibold text-text hover:text-accent">
                  {maintainer.name}
                </a>
                <div className="flex gap-3 text-xs text-muted">
                  <a href={maintainer.github} target="_blank" rel="noreferrer" className="hover:text-text">GitHub</a>
                  <a href={maintainer.linkedin} target="_blank" rel="noreferrer" className="hover:text-text">LinkedIn</a>
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href={config.githubUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-surface hover:bg-accentStrong"
                >
                  Contribute on GitHub
                </a>
                <a
                  href={config.pypiUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl border border-white/10 px-4 py-2 text-sm font-semibold text-text hover:border-accent"
                >
                  View on PyPI
                </a>
              </div>
            </div>
          </section>
        </main>

        <footer className="mt-12 flex flex-col items-center gap-3 border-t border-white/5 py-6 text-sm text-muted">
          <div className="flex items-center gap-2 text-text">
            <div className="h-3 w-3 rounded-full bg-accent" aria-hidden />
            VulnReach ©
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={config.githubUrl} target="_blank" rel="noreferrer" className="hover:text-text">
              GitHub
            </a>
            <a href={config.pypiUrl} target="_blank" rel="noreferrer" className="hover:text-text">
              PyPI
            </a>
            <a href={`${config.githubUrl}#readme`} target="_blank" rel="noreferrer" className="hover:text-text">
              Docs
            </a>
            <a href="/security.txt" className="hover:text-text">
              security.txt
            </a>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default App
